import { isStagingTestMode, stagingEmailMessage } from '../lib/stagingEnvironment'
import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { clearPortalMagicIntent, rememberPortalMagicIntent } from '../lib/portalAuth'

export function isValidPortalAccessPayload(payload) {
  return Boolean(payload?.access?.id && payload.access.status)
}

export default function PortalAccessPage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [access, setAccess] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const getCurrentSession = useCallback(async () => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get('code')

    if (code) {
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
      if (exchangeError) throw exchangeError
      clearPortalMagicIntent()
      window.history.replaceState({}, document.title, `/portal/access/${encodeURIComponent(token || '')}`)
    }

    const { data } = await supabase.auth.getSession()
    return data?.session || null
  }, [token])

  const activePortalExists = useCallback(async (session) => {
    if (!session?.access_token) return false

    const res = await fetch('/.netlify/functions/customer-portal-claim', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    const payload = await res.json().catch(() => ({}))
    return res.ok && isValidPortalAccessPayload(payload)
  }, [])

  const claimWithSession = useCallback(async (session) => {
    if (!session?.access_token || !token) return false

    setBusy(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ token }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not claim portal access.')
      if (!isValidPortalAccessPayload(payload)) throw new Error('Could not claim portal access.')
      navigate('/portal', { replace: true })
      return true
    } catch (err) {
      if (await activePortalExists(session)) {
        navigate('/portal', { replace: true })
        return true
      }
      setError(err.message || 'Could not claim portal access.')
      return false
    } finally {
      setBusy(false)
    }
  }, [activePortalExists, navigate, token])

  const claimIfSignedIn = useCallback(async () => {
    const session = await getCurrentSession()
    if (!session?.access_token) return false
    return claimWithSession(session)
  }, [claimWithSession, getCurrentSession])

  useEffect(() => {
    let alive = true

    async function load() {
      setLoading(true)
      setError('')
      try {
        if (await claimIfSignedIn()) return

        const res = await fetch('/.netlify/functions/customer-portal-claim?token=' + encodeURIComponent(token || ''))
        const payload = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(payload.error || 'This portal access link is invalid.')
        if (!isValidPortalAccessPayload(payload)) throw new Error('This portal access link is invalid.')
        if (!alive) return
        setAccess(payload.access)
      } catch (err) {
        if (alive) setError(err.message || 'This portal access link is invalid.')
      } finally {
        if (alive) setLoading(false)
      }
    }

    load()
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      claimWithSession(session)
    })

    return () => {
      alive = false
      subscription.unsubscribe()
    }
  }, [claimIfSignedIn, token])

  async function sendMagicLink() {
    if (!access?.invited_email) return
    setBusy(true)
    setError('')
    setSent(false)
    try {
      const portalAccessPath = `/portal/access/${encodeURIComponent(token || '')}`
      rememberPortalMagicIntent(portalAccessPath)
      if (isStagingTestMode()) throw new Error(stagingEmailMessage)
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: access.invited_email,
        options: {
          emailRedirectTo: `${window.location.origin}${portalAccessPath}`,
          shouldCreateUser: true,
        },
      })
      if (otpError) throw otpError
      setSent(true)
    } catch (err) {
      clearPortalMagicIntent()
      setError(err.message || 'Could not send magic login link.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Rebound</div>
        <h1>Rider profile access</h1>

        {loading ? (
          <div className="empty" style={{ minHeight: 120 }}><span className="spinner" style={{ width: 20, height: 20 }} /></div>
        ) : error ? (
          <div className="alert alert-error">{error}</div>
        ) : access ? (
          <>
            <p className="text-muted">
              {access.workshop_name} has enabled rider profile access for {access.customer_name}.
            </p>
            <div className="card" style={{ margin: '16px 0', padding: 14 }}>
              <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em' }}>Invited email</div>
              <div className="text-white fw7" style={{ marginTop: 4 }}>{access.invited_email_masked}</div>
            </div>
            {sent ? (
              <div className="alert alert-success">
                Secure login link sent. Open it from your email to finish access.
              </div>
            ) : (
              <button className="btn btn-primary" onClick={sendMagicLink} disabled={busy}>
                {busy ? 'Sending...' : 'Send secure login link'}
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  )
}
